/**
 * 출력 서식 카탈로그 패널 (환경설정 · 마스터 전용)
 * SSOT: D:\295\erp\CM\design\06_출력서식_카탈로그.md
 * — 출력—문서설정(GridPrintDialog)과 합치지 않음.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { useAuth } from 'contexts/AuthContext';
import { Card } from 'styles/common.styles';
import { SEARCH_LABEL_W, theme } from 'styles/theme';
import { FormArea, ActionButtonGroup, getCurrentFiscalYear } from 'components/forms';
import DraggableDialog from 'components/common/DraggableDialog';
import { showAlert } from 'utils/toastBridge';
import { openPrintWindow } from 'components/grid/print/printHtml';
import {
  REPORT_OPTIONS,
  REPORT_SCOPE,
} from 'system/reportCatalogSeed';
import {
  hydrateReportCatalog,
  listTemplates,
  listCompleteTemplates,
  isCompleteTemplate,
  getTemplate,
  getReport,
  getDataset,
  getActiveTemplateId,
  setTenantActiveTemplate,
  saveAsTenantCopy,
  updateTenantTemplate,
  suggestedDisplayName,
  getSampleRows,
} from 'system/reportCatalogStore';
import {
  PRINT_ALIGN_OPTIONS,
  PRINT_FONT_OPTIONS,
  PRINT_FONT_SIZE_OPTIONS,
  PRINT_ORIENT_OPTIONS,
  PRINT_PAPER_OPTIONS,
  PRINT_STAMP_COUNTS,
  PRINT_STAMP_POSITIONS,
  REPORT_LAYOUT_TOKENS,
  defaultStamp,
  buildReportPreviewHtml,
} from 'system/reportLayout';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
`;

const Panel = styled(Card)`
  padding: 10px 12px;
  overflow: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  th,
  td {
    border: 1px solid var(--erp-border);
    padding: 4px 8px;
    text-align: left;
  }
  th {
    background: var(--erp-surface-muted);
    font-weight: 600;
  }
  tr.active td {
    background: var(--erp-color-bg-selected, var(--erp-surface-muted));
  }
`;

const Meta = styled.p`
  margin: 0;
  font-size: 11px;
  color: var(--erp-text-secondary);
`;

const Mini = styled.input`
  min-height: 26px;
  padding: 3px 8px;
  font-size: 12px;
  border: 1px solid var(--erp-border);
  border-radius: 4px;
  box-sizing: border-box;
  width: ${(p) => p.$w || '100%'};
`;

const SelectMini = styled.select`
  min-height: 26px;
  padding: 3px 8px;
  font-size: 12px;
  border: 1px solid var(--erp-border);
  border-radius: 4px;
`;

const LayoutGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 8px 12px;
  margin-top: 8px;
`;

const Field = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  min-width: 0;
  span {
    flex: 0 0 72px;
    text-align: right;
    color: var(--erp-text-secondary);
  }
`;

const BandGrid = styled.div`
  display: grid;
  grid-template-columns: 56px 1fr 90px;
  gap: 6px 8px;
  align-items: center;
  margin-top: 6px;
  font-size: 12px;
`;

const TokenRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
`;

const TokenBtn = styled.button`
  min-height: 22px;
  padding: 2px 6px;
  font-size: 11px;
  border: 1px solid var(--erp-border);
  border-radius: 4px;
  background: var(--erp-surface-muted);
  color: var(--erp-text-primary);
  cursor: pointer;
`;

const PreviewFrame = styled.iframe`
  width: 100%;
  height: 100%;
  min-height: 420px;
  border: 1px solid var(--erp-border);
  background: ${theme.colors.white};
`;

const LoadSplit = styled.div`
  display: grid;
  grid-template-columns: minmax(220px, 280px) minmax(0, 1fr);
  gap: 8px;
  flex: 1;
  min-height: 0;
`;

const LoadList = styled.div`
  overflow: auto;
  border: 1px solid var(--erp-border);
  border-radius: 4px;
  min-height: 0;
`;

const LoadListItem = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  padding: 8px 10px;
  border: none;
  border-bottom: 1px solid var(--erp-border);
  background: ${(p) =>
    p.$active ? 'var(--erp-color-bg-selected, var(--erp-surface-muted))' : 'transparent'};
  color: var(--erp-text-primary);
  cursor: pointer;
  font-size: 12px;
  &:hover {
    background: var(--erp-color-bg-selected, var(--erp-surface-muted));
  }
`;

const LoadListTitle = styled.div`
  font-weight: 600;
`;

const LoadListMeta = styled.div`
  margin-top: 2px;
  font-size: 11px;
  color: var(--erp-text-secondary);
`;

const LoadPreviewPane = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  border: 1px solid var(--erp-border);
  border-radius: 4px;
  overflow: hidden;
`;

const LoadPreviewHint = styled.div`
  flex-shrink: 0;
  padding: 4px 8px;
  font-size: 11px;
  color: var(--erp-text-secondary);
  background: var(--erp-surface-muted);
  border-bottom: 1px solid var(--erp-border);
`;

const LoadPreviewFrame = styled.iframe`
  flex: 1;
  width: 100%;
  min-height: 0;
  border: none;
  background: ${theme.colors.white};
`;

const Denied = styled.div`
  padding: 16px 4px;
  font-size: 13px;
  color: var(--erp-text-secondary);
`;

function cloneDraft(tpl) {
  if (!tpl) return null;
  return {
    templateId: tpl.templateId,
    reportId: tpl.reportId,
    datasetId: tpl.datasetId,
    displayName: tpl.displayName,
    scope: tpl.scope,
    tenantCode: tpl.tenantCode || '',
    layout: JSON.parse(JSON.stringify(tpl.layout || {})),
    fieldMap: JSON.parse(JSON.stringify(tpl.fieldMap || [])),
    formatHints: { ...(tpl.formatHints || {}) },
  };
}

function templateScopeLabel(tpl, tenantCode) {
  if (!tpl) return '';
  if (tpl.scope === REPORT_SCOPE.DEFAULT) return '디폴트';
  const code = String(tpl.tenantCode || '').trim();
  if (code && code === tenantCode) return `기관본 · ${code}`;
  return code ? `타기관 · ${code}` : '기관본';
}

function templatePreviewHtml(tpl, { tenantCode, user } = {}) {
  if (!tpl) return '';
  const report = getReport(tpl.reportId);
  const dataset = getDataset(tpl.datasetId);
  if (!dataset) return '';
  const year = getCurrentFiscalYear();
  return buildReportPreviewHtml(tpl, dataset, getSampleRows(dataset.datasetId), {
    docName: tpl.layout?.title || report?.label || '',
    tenantCode,
    tenantName: tenantCode,
    userName: user?.userNm || user?.id || '',
    fiscalYear: year,
  });
}

export default function ReportCatalogPanel() {
  const tenantCode = useTenantCode();
  const { user, isMaster } = useAuth();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);
  const [reportId, setReportId] = useState(REPORT_OPTIONS[0]?.value || '');
  const [templates, setTemplates] = useState([]);
  const [activeId, setActiveId] = useState('');
  const [draft, setDraft] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [loadOpen, setLoadOpen] = useState(false);
  const [loadPickId, setLoadPickId] = useState('');
  const [bandFocus, setBandFocus] = useState(null);

  const methods = useForm({
    defaultValues: { reportId: REPORT_OPTIONS[0]?.value || '', displayName: '' },
  });
  const { setValue, getValues } = methods;

  const refresh = useCallback(async () => {
    try {
      await hydrateReportCatalog(tenantCode);
    } catch (e) {
      console.warn('[ReportCatalogPanel] hydrate', e);
    }
    const rid = getValues('reportId') || reportId;
    const list = listTemplates({ reportId: rid, tenantCode });
    const active = getActiveTemplateId(tenantCode, rid);
    setTemplates(list);
    setActiveId(active);
    setDraft((prev) => {
      if (prev && list.some((t) => t.templateId === prev.templateId)) return prev;
      return cloneDraft(
        list.find((t) => t.templateId === active) ||
          list.find((t) => t.scope === REPORT_SCOPE.DEFAULT) ||
          list[0] ||
          null
      );
    });
  }, [tenantCode, getValues, reportId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (draft && !String(getValues('displayName') || '').trim()) {
      setValue('displayName', draft.displayName || '');
    }
  }, [draft, getValues, setValue]);

  const report = getReport(draft?.reportId || reportId);
  const dataset = getDataset(draft?.datasetId || report?.datasetId);

  const completeList = useMemo(
    () => listCompleteTemplates({ reportId, tenantCode, includeOtherTenants: true }),
    [reportId, tenantCode, templates]
  );

  const loadPickTpl = useMemo(() => {
    if (!loadPickId) return null;
    return completeList.find((t) => t.templateId === loadPickId) || getTemplate(loadPickId);
  }, [loadPickId, completeList]);

  const loadPreviewHtml = useMemo(
    () => templatePreviewHtml(loadPickTpl, { tenantCode, user }),
    [loadPickTpl, tenantCode, user]
  );

  const applyTemplate = useCallback(
    (tpl) => {
      if (!tpl) return;
      setDraft(cloneDraft(tpl));
      setValue('displayName', tpl.displayName || '');
    },
    [setValue]
  );

  const handleOpenLoad = useCallback(() => {
    const rid = getValues('reportId') || reportId;
    const list = listCompleteTemplates({
      reportId: rid,
      tenantCode,
      includeOtherTenants: true,
    });
    if (!list.length) {
      showAlert(
        '불러올 완성 서식이 없습니다. 문서 서식과 데이터셋이 모두 설정된 양식만 선택할 수 있습니다.',
        'warning'
      );
      return;
    }
    const prefer =
      (draft?.templateId && list.some((t) => t.templateId === draft.templateId)
        ? draft.templateId
        : '') ||
      list.find((t) => t.scope === REPORT_SCOPE.DEFAULT)?.templateId ||
      list[0].templateId;
    setLoadPickId(prefer);
    setLoadOpen(true);
  }, [getValues, reportId, tenantCode, draft]);

  const handleConfirmLoad = useCallback(
    (maybeId) => {
      const id = typeof maybeId === 'string' ? maybeId : loadPickId;
      const tpl = getTemplate(id) || completeList.find((t) => t.templateId === id);
      if (!tpl || !isCompleteTemplate(tpl)) {
        showAlert('선택한 서식을 불러올 수 없습니다.', 'warning');
        return;
      }
      const rid = tpl.reportId;
      const fromOther =
        tpl.scope === REPORT_SCOPE.DEFAULT ||
        String(tpl.tenantCode || '') !== String(tenantCode || '');
      applyTemplate(tpl);
      if (fromOther) {
        setValue('displayName', suggestedDisplayName(rid, tenantCode));
        showAlert('서식을 불러왔습니다. 기관본은 다른 이름으로 저장하세요.', 'info');
      } else {
        showAlert('서식을 불러왔습니다.', 'info');
      }
      setLoadOpen(false);
    },
    [loadPickId, completeList, tenantCode, applyTemplate, setValue]
  );

  const collectPatch = useCallback(() => {
    if (!draft) return null;
    return {
      displayName: String(getValues('displayName') || draft.displayName || '').trim(),
      layout: draft.layout,
      fieldMap: draft.fieldMap,
      formatHints: draft.formatHints,
    };
  }, [draft, getValues]);

  const handleSave = useCallback(() => {
    if (!draft) return;
    const patch = collectPatch();
    try {
      const saved = updateTenantTemplate(draft.templateId, patch, tenantCode);
      applyTemplate(saved);
      refresh();
      showAlert(`저장했습니다. ${saved.displayName}`, 'success');
    } catch (e) {
      showAlert(e?.message || '저장할 수 없습니다.', 'warning');
    }
  }, [draft, collectPatch, tenantCode, applyTemplate, refresh]);

  const handleSaveAs = useCallback(() => {
    if (!draft) return;
    const rid = draft.reportId;
    const name =
      String(getValues('displayName') || '').trim() || suggestedDisplayName(rid, tenantCode);
    try {
      const saved = saveAsTenantCopy({
        fromTemplateId: draft.templateId,
        tenantCode,
        displayName: name,
        layout: draft.layout,
        fieldMap: draft.fieldMap,
        formatHints: draft.formatHints,
      });
      applyTemplate(saved);
      refresh();
      showAlert(`기관본으로 저장했습니다. ${saved.displayName}`, 'success');
    } catch (e) {
      showAlert(e?.message || '저장 실패', 'error');
    }
  }, [draft, getValues, tenantCode, applyTemplate, refresh]);

  const handleActivate = useCallback(() => {
    if (!draft?.templateId) {
      showAlert('서식을 선택하세요.', 'warning');
      return;
    }
    try {
      setTenantActiveTemplate(tenantCode, draft.reportId, draft.templateId);
      refresh();
      showAlert(`활성 서식: ${draft.displayName || draft.templateId}`, 'success');
    } catch (e) {
      showAlert(e?.message || '활성 지정 실패', 'error');
    }
  }, [draft, tenantCode, refresh]);

  const previewHtml = useMemo(
    () => templatePreviewHtml(draft, { tenantCode, user }),
    [draft, tenantCode, user]
  );

  const handlePreview = useCallback(() => {
    if (!draft) {
      showAlert('미리볼 서식이 없습니다.', 'warning');
      return;
    }
    setPreviewOpen(true);
  }, [draft]);

  const patchLayout = useCallback((partial) => {
    setDraft((prev) => (prev ? { ...prev, layout: { ...prev.layout, ...partial } } : prev));
  }, []);

  const patchBand = useCallback((band, zone, partial) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const cur = prev.layout?.[band] || {};
      return {
        ...prev,
        layout: {
          ...prev.layout,
          [band]: { ...cur, [zone]: { ...(cur[zone] || {}), ...partial } },
        },
      };
    });
  }, []);

  const insertToken = useCallback(
    (token) => {
      if (!bandFocus || !draft) return;
      const [band, zone] = bandFocus;
      const cell = draft.layout?.[band]?.[zone];
      patchBand(band, zone, { text: `${cell?.text || ''}${token}` });
    },
    [bandFocus, draft, patchBand]
  );

  const patchField = useCallback((field, partial) => {
    setDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        fieldMap: (prev.fieldMap || []).map((f) => (f.field === field ? { ...f, ...partial } : f)),
      };
    });
  }, []);

  const moveField = useCallback((field, dir) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const list = [...(prev.fieldMap || [])].sort((a, b) => a.order - b.order);
      const i = list.findIndex((f) => f.field === field);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= list.length) return prev;
      const tmp = list[i];
      list[i] = list[j];
      list[j] = tmp;
      return {
        ...prev,
        fieldMap: list.map((f, idx) => ({ ...f, order: (idx + 1) * 10 })),
      };
    });
  }, []);

  const formFields = useMemo(
    () => [
      [
        {
          name: 'reportId',
          label: '문서종류',
          type: 'select',
          width: 180,
          labelWidth: SEARCH_LABEL_W,
          options: REPORT_OPTIONS,
        },
        {
          name: 'displayName',
          label: '표시명',
          type: 'text',
          width: 240,
          labelWidth: SEARCH_LABEL_W,
        },
      ],
    ],
    []
  );

  const canOverwrite = draft?.scope === REPORT_SCOPE.TENANT && draft?.tenantCode === tenantCode;

  const actionButtons = [
    { id: 'refresh', label: '새로고침', onClick: refresh },
    { id: 'load', label: '불러오기', onClick: handleOpenLoad },
    { id: 'save', label: '저장', onClick: handleSave, disabled: !canOverwrite, variant: 'primary' },
    { id: 'saveAs', label: '다른 이름으로 저장', onClick: handleSaveAs, variant: 'success' },
    { id: 'active', label: '활성', onClick: handleActivate, disabled: !draft },
    { id: 'preview', label: '미리보기', onClick: handlePreview, disabled: !draft },
  ];

  if (!isMaster) {
    return <Denied>서식 카탈로그는 시스템 마스터만 사용할 수 있습니다.</Denied>;
  }

  const layout = draft?.layout || {};
  const stamp = layout.stamp || defaultStamp(3);
  const margin = layout.marginMm || { top: 15, right: 15, bottom: 15, left: 15 };

  return (
    <Stack>
      <Meta>
        기관 표 리포트 디자인. 조회 화면 출력(개인 문서설정)과 저장소가 다릅니다. prefs={prefsUserId}
      </Meta>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} />
        <FormArea
          dense
          fields={formFields}
          onFormChange={(formData, _prev, changed) => {
            if (changed === 'reportId' && formData?.reportId) {
              setReportId(formData.reportId);
              setDraft(null);
              setTimeout(refresh, 0);
            }
          }}
        />
      </FormProvider>
      <Meta>
        문서={report?.label || reportId} · dataset={dataset?.datasetId || '—'} · 활성=
        {activeId || '—'} · 선택={draft?.templateId || '—'}
        {draft?.scope === REPORT_SCOPE.DEFAULT ? ' · 디폴트는 다른 이름으로만 저장' : ''}
      </Meta>

      <Panel>
        <strong style={{ fontSize: 13 }}>서식 목록</strong>
        <Table>
          <thead>
            <tr>
              <th>표시명</th>
              <th>scope</th>
              <th>기관</th>
              <th>templateId</th>
              <th>활성</th>
            </tr>
          </thead>
          <tbody>
            {templates.map((t) => (
              <tr
                key={t.templateId}
                className={draft?.templateId === t.templateId ? 'active' : ''}
                style={{ cursor: 'pointer' }}
                onClick={() => applyTemplate(t)}
                onDoubleClick={() => {
                  applyTemplate(t);
                  setPreviewOpen(true);
                }}
              >
                <td>{t.displayName}</td>
                <td>{t.scope}</td>
                <td>{t.tenantCode || '—'}</td>
                <td>
                  <code>{t.templateId}</code>
                </td>
                <td>{activeId === t.templateId ? 'Y' : ''}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Panel>

      <Panel>
        <strong style={{ fontSize: 13 }}>필드 매핑</strong>
        <Meta>dataset 필드만 매핑. 표시 순서 10 단위. 열 폭 mm.</Meta>
        {!draft ? (
          <Meta>목록에서 서식을 선택하세요.</Meta>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>표시</th>
                <th>순서</th>
                <th>필드</th>
                <th>표시명</th>
                <th>너비mm</th>
                <th>정렬</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(draft.fieldMap || []).map((f) => (
                <tr key={f.field}>
                  <td>
                    <input
                      type="checkbox"
                      checked={f.visible !== false}
                      onChange={(e) => patchField(f.field, { visible: e.target.checked })}
                    />
                  </td>
                  <td>{f.order}</td>
                  <td>
                    <code>{f.field}</code>
                  </td>
                  <td>
                    <Mini
                      value={f.label || ''}
                      onChange={(e) => patchField(f.field, { label: e.target.value })}
                    />
                  </td>
                  <td>
                    <Mini
                      $w="64px"
                      type="number"
                      min={8}
                      max={80}
                      value={f.widthMm}
                      onChange={(e) => patchField(f.field, { widthMm: Number(e.target.value) })}
                    />
                  </td>
                  <td>
                    <SelectMini
                      value={f.align || 'left'}
                      onChange={(e) => patchField(f.field, { align: e.target.value })}
                    >
                      {PRINT_ALIGN_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </SelectMini>
                  </td>
                  <td>
                    <TokenBtn type="button" onClick={() => moveField(f.field, -1)}>
                      ↑
                    </TokenBtn>{' '}
                    <TokenBtn type="button" onClick={() => moveField(f.field, 1)}>
                      ↓
                    </TokenBtn>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Panel>

      {draft ? (
        <Panel>
          <strong style={{ fontSize: 13 }}>레이아웃</strong>
          <LayoutGrid>
            <Field>
              <span>제목</span>
              <Mini
                value={layout.title || ''}
                onChange={(e) => patchLayout({ title: e.target.value })}
              />
            </Field>
            <Field>
              <span>용지</span>
              <SelectMini
                value={layout.paper || 'A4'}
                onChange={(e) => patchLayout({ paper: e.target.value })}
              >
                {PRINT_PAPER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </SelectMini>
            </Field>
            <Field>
              <span>방향</span>
              <SelectMini
                value={layout.orientation || 'landscape'}
                onChange={(e) => patchLayout({ orientation: e.target.value })}
              >
                {PRINT_ORIENT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </SelectMini>
            </Field>
            <Field>
              <span>글꼴</span>
              <SelectMini
                value={layout.fontFamily || 'Pretendard'}
                onChange={(e) => patchLayout({ fontFamily: e.target.value })}
              >
                {PRINT_FONT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </SelectMini>
            </Field>
            <Field>
              <span>크기</span>
              <SelectMini
                value={layout.fontSizePt || 9}
                onChange={(e) => patchLayout({ fontSizePt: Number(e.target.value) })}
              >
                {PRINT_FONT_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}pt
                  </option>
                ))}
              </SelectMini>
            </Field>
            <Field>
              <span>행번호</span>
              <input
                type="checkbox"
                checked={layout.showRowNumber !== false}
                onChange={(e) => patchLayout({ showRowNumber: e.target.checked })}
              />
            </Field>
            {['top', 'right', 'bottom', 'left'].map((side) => (
              <Field key={side}>
                <span>여백{side}</span>
                <Mini
                  $w="72px"
                  type="number"
                  min={5}
                  max={40}
                  value={margin[side]}
                  onChange={(e) =>
                    patchLayout({
                      marginMm: { ...margin, [side]: Number(e.target.value) },
                    })
                  }
                />
              </Field>
            ))}
          </LayoutGrid>

          <Meta style={{ marginTop: 10 }}>머릿글</Meta>
          <BandGrid>
            {['left', 'center', 'right'].map((zone) => (
              <React.Fragment key={`h-${zone}`}>
                <span>{zone}</span>
                <Mini
                  value={layout.header?.[zone]?.text || ''}
                  onFocus={() => setBandFocus(['header', zone])}
                  onChange={(e) => patchBand('header', zone, { text: e.target.value })}
                />
                <SelectMini
                  value={layout.header?.[zone]?.align || 'left'}
                  onChange={(e) => patchBand('header', zone, { align: e.target.value })}
                >
                  {PRINT_ALIGN_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </SelectMini>
              </React.Fragment>
            ))}
          </BandGrid>
          <Meta style={{ marginTop: 10 }}>꼬릿말</Meta>
          <BandGrid>
            {['left', 'center', 'right'].map((zone) => (
              <React.Fragment key={`f-${zone}`}>
                <span>{zone}</span>
                <Mini
                  value={layout.footer?.[zone]?.text || ''}
                  onFocus={() => setBandFocus(['footer', zone])}
                  onChange={(e) => patchBand('footer', zone, { text: e.target.value })}
                />
                <SelectMini
                  value={layout.footer?.[zone]?.align || 'left'}
                  onChange={(e) => patchBand('footer', zone, { align: e.target.value })}
                >
                  {PRINT_ALIGN_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </SelectMini>
              </React.Fragment>
            ))}
          </BandGrid>
          <TokenRow>
            {REPORT_LAYOUT_TOKENS.map((t) => (
              <TokenBtn key={t.token} type="button" onClick={() => insertToken(t.token)}>
                {t.token}
              </TokenBtn>
            ))}
          </TokenRow>

          <Meta style={{ marginTop: 10 }}>결재선</Meta>
          <LayoutGrid>
            <Field>
              <span>사용</span>
              <input
                type="checkbox"
                checked={stamp.enabled === true}
                onChange={(e) => patchLayout({ stamp: { ...stamp, enabled: e.target.checked } })}
              />
            </Field>
            <Field>
              <span>칸수</span>
              <SelectMini
                value={stamp.cellCount || 3}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  patchLayout({
                    stamp: {
                      ...defaultStamp(n),
                      enabled: stamp.enabled === true,
                      position: stamp.position || 'titleRight',
                    },
                  });
                }}
              >
                {PRINT_STAMP_COUNTS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </SelectMini>
            </Field>
            <Field>
              <span>위치</span>
              <SelectMini
                value={stamp.position || 'titleRight'}
                onChange={(e) => patchLayout({ stamp: { ...stamp, position: e.target.value } })}
              >
                {PRINT_STAMP_POSITIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </SelectMini>
            </Field>
          </LayoutGrid>
        </Panel>
      ) : null}

      <DraggableDialog
        open={loadOpen}
        onClose={() => setLoadOpen(false)}
        title="서식 불러오기"
        dialogWidth="1080px"
        dialogHeight="720px"
        prefsUserId={prefsUserId}
        sizeKey="reportCatalogLoad"
      >
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, gap: 8 }}>
          <ActionButtonGroup
            buttons={[
              {
                id: 'loadConfirm',
                label: '불러오기',
                onClick: handleConfirmLoad,
                disabled: !loadPickTpl,
                variant: 'primary',
              },
              { id: 'loadClose', label: '닫기', onClick: () => setLoadOpen(false) },
            ]}
          />
          <Meta>
            문서 서식과 데이터셋이 모두 설정된 완성본만 표시합니다. 선택하면 오른쪽에 샘플 미리보기가 나옵니다.
          </Meta>
          <LoadSplit>
            <LoadList>
              {completeList.map((t) => (
                <LoadListItem
                  key={t.templateId}
                  type="button"
                  $active={t.templateId === loadPickId}
                  onClick={() => setLoadPickId(t.templateId)}
                  onDoubleClick={() => handleConfirmLoad(t.templateId)}
                >
                  <LoadListTitle>{t.displayName || t.templateId}</LoadListTitle>
                  <LoadListMeta>
                    {templateScopeLabel(t, tenantCode)}
                    {t.templateId === activeId ? ' · 활성' : ''}
                  </LoadListMeta>
                </LoadListItem>
              ))}
            </LoadList>
            <LoadPreviewPane>
              <LoadPreviewHint>
                {loadPickTpl
                  ? `${loadPickTpl.displayName || ''} (${templateScopeLabel(loadPickTpl, tenantCode)})`
                  : '왼쪽에서 서식을 선택하세요'}
              </LoadPreviewHint>
              <LoadPreviewFrame title="불러오기 미리보기" srcDoc={loadPreviewHtml} />
            </LoadPreviewPane>
          </LoadSplit>
        </div>
      </DraggableDialog>

      <DraggableDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title="서식 미리보기 (샘플)"
        dialogWidth="960px"
        dialogHeight="720px"
        prefsUserId={prefsUserId}
        sizeKey="reportCatalogPreview"
      >
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, gap: 8 }}>
          <ActionButtonGroup
            buttons={[
              {
                id: 'print',
                label: '인쇄',
                onClick: () => {
                  const r = openPrintWindow(previewHtml);
                  if (!r.ok) showAlert(r.reason || '인쇄 창을 열 수 없습니다.', 'warning');
                },
              },
            ]}
          />
          <PreviewFrame title="서식 미리보기" srcDoc={previewHtml} />
        </div>
      </DraggableDialog>
    </Stack>
  );
}
